import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface RepOption {
  id: string
  name: string
}

interface RepMultiSelectProps {
  /** Selectable reps (already filtered to active sales users). */
  reps: RepOption[]
  /** Currently selected rep ids. */
  value: string[]
  /** Called with the next selected-id array. */
  onChange: (ids: string[]) => void
  /** Max reps that can be selected at once (keeps chart colours distinct). */
  max?: number
  id?: string
  className?: string
}

/**
 * Admin-only multi-select for the Sales Forecast page. Empty selection means
 * "All Reps" (aggregate); one or more selections drive single/overlay modes.
 * Selection is capped at `max` so each rep keeps a distinct chart colour.
 */
export function RepMultiSelect({
  reps,
  value,
  onChange,
  max = 5,
  id,
  className,
}: RepMultiSelectProps) {
  const [open, setOpen] = React.useState(false)

  const selectedSet = React.useMemo(() => new Set(value), [value])
  const atMax = value.length >= max

  const toggle = React.useCallback(
    (repId: string) => {
      if (selectedSet.has(repId)) {
        onChange(value.filter((v) => v !== repId))
      } else if (!atMax) {
        onChange([...value, repId])
      }
    },
    [atMax, onChange, selectedSet, value]
  )

  const clear = React.useCallback(() => onChange([]), [onChange])

  const selectedReps = React.useMemo(
    () => reps.filter((r) => selectedSet.has(r.id)),
    [reps, selectedSet]
  )

  const listboxId = id ? `${id}-listbox` : undefined

  const triggerLabel =
    selectedReps.length === 0
      ? 'All Reps'
      : selectedReps.length === 1
        ? (selectedReps[0]?.name ?? '1 rep selected')
        : `${selectedReps.length} reps selected`

  return (
    <div className={className}>
      <span id={`${id ?? 'rep-multi'}-label`} className="text-xs text-muted-foreground mb-1 block">
        Sales Reps
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-label={`Select sales reps to compare. ${
              selectedReps.length === 0
                ? 'All reps shown.'
                : `${selectedReps.length} of max ${max} selected.`
            }`}
            className="w-[240px] h-9 justify-between font-normal"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="end" onWheel={(e) => e.stopPropagation()}>
          <Command>
            <CommandInput placeholder="Search reps..." />
            <CommandList
              id={listboxId}
              role="listbox"
              aria-label="Sales reps"
              className="max-h-[320px]"
            >
              <CommandEmpty>No reps found.</CommandEmpty>
              <CommandGroup>
                {reps.map((rep) => {
                  const isSelected = selectedSet.has(rep.id)
                  const disabled = !isSelected && atMax
                  return (
                    <CommandItem
                      key={rep.id}
                      value={rep.name}
                      disabled={disabled}
                      onSelect={() => toggle(rep.id)}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(disabled && 'opacity-50')}
                    >
                      <span
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                          isSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-muted-foreground/40'
                        )}
                        aria-hidden="true"
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate">{rep.name}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
            <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
              <span className="text-muted-foreground" aria-live="polite">
                {atMax ? `Max ${max} reps` : `${value.length}/${max} selected`}
              </span>
              {value.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={clear}
                  aria-label="Clear selected reps and show all"
                >
                  <X className="mr-1 h-3 w-3" aria-hidden="true" />
                  Clear
                </Button>
              )}
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedReps.length > 1 && (
        <div className="mt-1.5 flex flex-wrap gap-1" aria-hidden="true">
          {selectedReps.map((rep) => (
            <Badge key={rep.id} variant="secondary" className="font-normal">
              {rep.name}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
